import { NumberData, TableData } from '@/types/common';
import { HarnessesDataState } from '@/types/master/harness';
import axios from 'axios';
import { getAccessToken } from '@/libs/firebaseAuth';
import { getBaseUrl } from '@/libs/env';

export const deviceStatus: NumberData = {
  0: '未設定',
  1: '準備中',
  2: '検証済',
  3: '使用中',
  4: '使用不可',
  5: '稼働停止'
};

export const deviceType: NumberData = {
  1: 'MCCS Standard 2.0',
  2: 'MCCS II 3rd gen',
  3: 'MCCS III ',
  11: 'MCCS Type T'
};

/** MCCS Type T（deviceType 11） */
export const isMccsTypeT = (dt: number) => dt === 11;

/** Company group (FC) that identifies Tokai Rika companies via companyLinks. */
export const TOKAI_RIKA_COMPANY_GROUP_ID = 227;
export const TOKAI_RIKA_ITEM_ID = 660;

/**
 * Non-Tokai (regular) MasMinor → itemId.
 * J01 / J02 keep legacy mapping; J03 is shared.
 */
export const MAST_MINOR_ITEM_ID: Record<string, number> = {
  J01: 434,
  J02: 494,
  J03: 691
};

/**
 * Tokai Rika MasMinor → itemId.
 * J02A and J03 are specials; any other Tokai minor falls back to 660.
 */
export const MAST_TOKAI_RIKA_MINOR_ITEM_ID: Record<string, number> = {
  J02A: 690,
  J03: 691
};

/**
 * @deprecated Use resolveMastMinorItemId(minor, isTokaiRika) — flat map ignores Tokai rules.
 * Kept as the regular (non-Tokai) map for callers that only need J01/J02/J03.
 */
export const deviceItemId: TableData = { ...MAST_MINOR_ITEM_ID };

/**
 * Resolve itemId from deviceMinorVersion + Tokai flag (aligned with DAIS mastDeviceMapping).
 *
 * - Tokai + J02A → 690
 * - J03 → 691 (Tokai or not)
 * - Tokai (other) → 660
 * - Non-Tokai + J01 / J02 → legacy map
 * - Else → null
 */
export function resolveMastMinorItemId(
  deviceMinorVersion: string | number | null | undefined,
  isTokaiRika: boolean
): number | null {
  const minor =
    deviceMinorVersion != null && String(deviceMinorVersion).trim() !== ''
      ? String(deviceMinorVersion).trim()
      : null;

  if (isTokaiRika) {
    if (minor != null && MAST_TOKAI_RIKA_MINOR_ITEM_ID[minor] != null) {
      return MAST_TOKAI_RIKA_MINOR_ITEM_ID[minor];
    }
    return TOKAI_RIKA_ITEM_ID;
  }

  if (minor != null && MAST_MINOR_ITEM_ID[minor] != null) {
    return MAST_MINOR_ITEM_ID[minor];
  }

  return null;
}

export function isTokaiRikaOrderCompany(
  orderCompanyId: number | null | undefined,
  tokaiRikaCompanyIds: Set<number> | number[]
): boolean {
  if (orderCompanyId == null) return false;
  if (tokaiRikaCompanyIds instanceof Set) {
    return tokaiRikaCompanyIds.has(orderCompanyId);
  }
  return tokaiRikaCompanyIds.includes(orderCompanyId);
}

/** Load affiliated company IDs for Tokai Rika FC (group 227). */
export async function fetchTokaiRikaCompanyIds(): Promise<Set<number>> {
  try {
    const response = await axios.get(
      `${getBaseUrl()}/master/company-groups/${TOKAI_RIKA_COMPANY_GROUP_ID}`,
      { headers: { auth: await getAccessToken() } }
    );
    const companies = response.data?.affiliatedCompanies ?? [];
    return new Set(
      companies.map((company: { id: number }) => Number(company.id))
    );
  } catch {
    return new Set();
  }
}

/** MCCSⅢ serial prefix (端末シリアル番号登録) */
export const getMccsIiiSerialInitial = (deviceMinorVersion: string): string => {
  switch (deviceMinorVersion) {
    case 'J01':
      return 'MS03J1';
    case 'J02':
    case 'J02A':
      return 'MS03J2';
    case 'J03':
      return 'MS03J3';
    default:
      return 'MS03J2';
  }
};

export const deviceTypeList = [
  {
    id: 1,
    name: 'MCCS Standard 2.0'
  },
  {
    id: 2,
    name: 'MCCS II 3rd gen'
  },
  {
    id: 3,
    name: 'MCCS III '
  },
  {
    id: 11,
    name: 'MCCS Type T'
  }
];

export const deviceMinorVersionList = [
  { name: 'J01' },
  { name: 'J02' },
  { name: 'J02A' },
  { name: 'J03' }
];

/** MCCS選択肢: deviceType-harnessType 形式 */
export const mccsOptions = [
  { deviceType: 3, harnessType: 3, label: 'MCCSⅢ' },
  { deviceType: 2, harnessType: 2, label: 'MCCSⅡ' },
  { deviceType: 2, harnessType: 3, label: 'MCCSⅡ R' },
  { deviceType: 11, harnessType: 11, label: 'Type T' },
  { deviceType: 11, harnessType: 3, label: 'Type T（MCCSⅢ専用ハーネス）' }
] as const;

export const mccsDeviceType: Record<number, string> = {
  3: 'MCCSⅢ',
  2: 'MCCSⅡ',
  11: 'MCCS Type T '
};

export const harnessType: Record<number, string> = {
  3: 'MCCSⅢ用',
  2: 'MCCSⅡ用',
  11: 'MCCS Type T用'
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validateSerial = (rule: any, value: string, callback: any) => {
  if (value.match(/^[A-Za-z0-9]{16}$/)) {
    return callback();
  }
  if (value.match(/^[A-Za-z0-9]{10}$/)) {
    return callback();
  }
  callback(new Error('10桁または16桁の英数字で入力してください。'));
};

export const verifyEditRule = {
  serialCode: [
    { required: true, message: '端末シリアル番号は必須です。' },
    { validator: validateSerial, trigger: 'blur' }
  ],
  firmVersion: [{ required: true, message: '出荷ファームVerは必須です。' }],
  internalBatteryVoltage: [
    { required: true, message: 'IBバッテリーは必須です。' }
  ],
  mboot: [{ required: true, message: 'Mbootは必須です。' }]
};

export const baseEditRule = {
  serialCode: [
    { required: true, message: '端末シリアル番号は必須です。' },
    { validator: validateSerial, trigger: 'blur' }
  ],
  firmVersion: [{ required: true, message: '出荷ファームVerは必須です。' }],
  internalBatteryVoltage: [
    { required: true, message: 'IBバッテリーは必須です。' }
  ],
  mboot: [{ required: true, message: 'Mbootは必須です。' }]
};

// deviceType に応じてルールを返す関数
export const getEditRule = (deviceType: number) => {
  if (deviceType === 11) {
    return {
      serialCode: [{ required: true, message: '端末シリアル番号は必須です。' }]
    };
  }
  return baseEditRule;
};

export const handlingType: NumberData = {
  1: '新品',
  2: '流用',
  3: 'リビルド',
  4: '中古',
  5: '不具合',
  6: '処分'
};

export const omitHandlingType: NumberData = {
  1: '新品',
  2: '流用',
  3: 'リビ',
  4: '中古',
  5: '不具合',
  6: '処分'
};

export const simCarrier: NumberData = {
  0: 'その他',
  1: 'SOFTBANK',
  2: 'SORACOM'
};

export const simStatus: Record<string, string> = {
  //SORACOM
  ready: 'Ready',
  testing: 'Testing',
  active: 'Active',
  inactive: 'Inactive',
  terminated: 'Terminated',
  standy: 'Standy',
  suspended: 'Suspended',
  //SOFTBANK
  'ACTIVATION READY': 'Activation Ready',
  ACTIVATED: 'Activated',
  DEACTIVATED: 'Deactivated',
  RETIRED: 'Retired',
  TEST_READY: 'Test Ready'
};

export const handlingTypeList = [
  {
    id: 1,
    name: '新品'
  },
  {
    id: 2,
    name: '流用'
  },
  {
    id: 3,
    name: 'リビルド'
  },
  {
    id: 4,
    name: '中古'
  },
  {
    id: 5,
    name: '不具合'
  },
  {
    id: 6,
    name: '処分'
  }
];

export const deviceStatusList = [
  {
    id: 0,
    name: '未設定'
  },
  {
    id: 1,
    name: '準備中'
  },
  {
    id: 2,
    name: '検証済'
  },
  {
    id: 3,
    name: '使用中'
  },
  {
    id: 4,
    name: '使用不可'
  },
  {
    id: 5,
    name: '稼働停止'
  }
];

//TODO: この関数が呼ばれた時点でstoreにharnessMaster.listDataが入っていることが保証されていない。
//しかしモジュールからstore.dispatch('harnessesMaster/fetch')を呼び出せない。どうする
export const getHarnessByDeviceType = async (
  listData: HarnessesDataState[],
  harnessId: number,
  deviceType: number | null
) => {
  if (!deviceType) {
    deviceType = 2;
  }
  const foundItem: HarnessesDataState | undefined = listData.find(
    (item: HarnessesDataState) =>
      item.deviceType === deviceType && item.id === harnessId
  );
  return foundItem;
};
